import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { analyticsListeners } from '~/analytics';
import { appointmentDraftActions, appointmentDraftSelectors } from '~/api';
import { envConfigSelectors, loadingSpinnerActions } from '~/components';
import { areSlotsEqual } from '~/components/calendar-with-filters/time-slots/helpers';
import { selectDateActions, selectDateSelectors } from '~/routes/select-date';
import { servicesSelectors } from '~/selectors';
import { Calendar } from './calendar';
import styles from './CalendarWithFilters.scss';
import { DateFilters } from './date-filters';
import { TimeSlots } from './time-slots';

export class CalendarWithFilters extends React.PureComponent {
  componentDidUpdate(prevProps) {
    const {
      mainAppointmentType: { slots },
      dispatchSelectDateSlot,
    } = this.props;

    const {
      mainAppointmentType: { slots: prevSlots },
    } = prevProps;

    if (!slots.length && prevSlots.length) {
      dispatchSelectDateSlot('');
    }
  }

  selectDateSlot = (selectedDateSlot) => {
    const { isEditMode, dispatchSelectDateSlot } = this.props;

    if (selectedDateSlot) {
      dispatchSelectDateSlot(selectedDateSlot.date);
    } else {
      dispatchSelectDateSlot(null);
    }

    if (isEditMode) {
      analyticsListeners.triggerEditPageChangesMadeAnalytics(
        'appointments',
        'appointments:date/time',
      );
    }
  };

  render() {
    const {
      extraClassName,
      timeSlotsList,
      timeSlotSectionTitle,
      children,
      loadNextSlots,
      loadPreviousSlots,
      maxAdvance,
      eligibleSlotsFilter,
      selectedPartner,
      formatTimeSlots,
      selectedDateSlot,
      areSlotsEqualFun,

      filteredSlots,
      filters,
      isFiltersHidden,
      mainAppointmentType,
      relatedAppointmentType,

      isApiCallFinished,
      dispatchApplyFilters,
      dispatchSetMainAppointmentTimeSlot,
      dispatchSetRelatedAppointmentTimeSlot,
      dispatchApiCallFinished,
    } = this.props;

    const [dateSlotMessage, timeSlotMessage, calendarTimeSlotsIntro] =
      React.Children.toArray(children);

    return (
      filteredSlots && (
        <Fragment>
          {!isFiltersHidden && (
            <DateFilters activeFilters={filters} onChange={dispatchApplyFilters} />
          )}
          {dateSlotMessage}
          <div className={styles[extraClassName]}>
            {!!filteredSlots.length && (
              <Calendar
                key={selectedPartner.id || 'default'}
                selectedPartnerId={selectedPartner.id}
                months={filteredSlots}
                selectedDateSlot={selectedDateSlot.date}
                selectDateSlot={this.selectDateSlot}
                loadNextSlots={loadNextSlots}
                loadPreviousSlots={loadPreviousSlots}
                apiCallFinished={dispatchApiCallFinished}
                maxAdvance={maxAdvance}
                eligibleSlotsFilter={eligibleSlotsFilter}
                isApiCallFinished={isApiCallFinished}
              />
            )}
            {!!filteredSlots.length && !!selectedDateSlot.times.length && (
              <TimeSlots
                mainAppointmentType={mainAppointmentType}
                relatedAppointmentType={relatedAppointmentType}
                timeSlotsList={timeSlotsList}
                timeSlotSectionTitle={timeSlotSectionTitle}
                formatSlots={formatTimeSlots}
                areSlotsEqual={areSlotsEqualFun}
                dispatchSetMainAppointmentTimeSlot={dispatchSetMainAppointmentTimeSlot}
                dispatchSetRelatedAppointmentTimeSlot={dispatchSetRelatedAppointmentTimeSlot}
              >
                {calendarTimeSlotsIntro}
                {timeSlotMessage}
              </TimeSlots>
            )}
          </div>
        </Fragment>
      )
    );
  }
}

export const mapStateToProps = (state, ownProps) => {
  const mainAppointmentType = appointmentDraftSelectors.selectAppointmentType(state);
  const relatedAppointmentType =
    appointmentDraftSelectors.selectRelatedAppointmentTypeById()(state);
  const isMultipleAppointment = appointmentDraftSelectors.isMultipleAppointment(state);

  const remoteConfig = envConfigSelectors.remoteConfig(mainAppointmentType.id)(state);
  const calendarConfig = servicesSelectors.selectCalendarConfig(state);

  return {
    filteredSlots: selectDateSelectors.selectFilteredTimeSlots(mainAppointmentType.id)(state),
    filters: selectDateSelectors.selectFilters(state),
    isFiltersHidden:
      remoteConfig.hideFilters || calendarConfig.hideFilters || !!ownProps.isFiltersHidden,
    areSlotsEqualFun: calendarConfig.areSlotsEqual || areSlotsEqual,
    mainAppointmentType: {
      appointmentType: mainAppointmentType,
      slots: selectDateSelectors.selectFilteredTimeSlotsBySelectedDate(mainAppointmentType.id)(
        state,
      ),
      selectedTimeSlot: appointmentDraftSelectors.selectSelectedTimeSlotById(
        mainAppointmentType.id,
      )(state),
      timeSlotAccordionTitle: isMultipleAppointment ? 'First treatment' : undefined,
    },
    relatedAppointmentType: isMultipleAppointment
      ? {
          appointmentType: relatedAppointmentType,
          slots: selectDateSelectors.selectFilteredTimeSlotsBySelectedDate(
            relatedAppointmentType.id,
          )(state),
          selectedTimeSlot: appointmentDraftSelectors.selectSelectedTimeSlotById(
            relatedAppointmentType.id,
          )(state),
          timeSlotAccordionTitle: 'Second treatment',
        }
      : null,
  };
};

const mapDispatchToProps = {
  dispatchApplyFilters: selectDateActions.setFilters,
  dispatchApiCallFinished: loadingSpinnerActions.apiCallFinished,
  dispatchSelectDateSlot: appointmentDraftActions.setDateSlot,

  dispatchSetMainAppointmentTimeSlot: appointmentDraftActions.setTimeSlot,
  dispatchSetRelatedAppointmentTimeSlot: appointmentDraftActions.setRelatedAppointmentTimeSlot,
};

export const ConnectedCalendarWithFilters = connect(
  mapStateToProps,
  mapDispatchToProps,
)(CalendarWithFilters);

CalendarWithFilters.displayName = 'CalendarWithFilters';

CalendarWithFilters.propTypes = {
  filters: PropTypes.arrayOf(PropTypes.string),
  extraClassName: PropTypes.string,
  isApiCallFinished: PropTypes.bool.isRequired,
  isEditMode: PropTypes.bool,
  maxAdvance: PropTypes.number.isRequired,
  selectedDateSlot: PropTypes.shape({
    date: PropTypes.string.isRequired,
    times: PropTypes.arrayOf(
      PropTypes.shape({
        eventId: PropTypes.string.isRequired,
        from: PropTypes.number.isRequired,
        to: PropTypes.number,
      }),
    ),
  }).isRequired,
  filteredSlots: PropTypes.arrayOf(
    PropTypes.shape({
      month: PropTypes.number.isRequired,
      year: PropTypes.number.isRequired,
      dates: PropTypes.arrayOf(
        PropTypes.shape({
          date: PropTypes.string.isRequired,
          times: PropTypes.arrayOf(
            PropTypes.shape({
              from: PropTypes.number.isRequired,
              to: PropTypes.number.isRequired,
            }),
          ).isRequired,
        }),
      ),
    }).isRequired,
  ).isRequired,
  mainAppointmentType: PropTypes.shape({
    selectedTimeSlot: PropTypes.shape({
      from: PropTypes.number.isRequired,
      to: PropTypes.number,
    }).isRequired,
  }).isRequired,
  relatedAppointmentType: PropTypes.shape({
    selectedTimeSlot: PropTypes.shape({
      from: PropTypes.number.isRequired,
      to: PropTypes.number,
    }),
  }),
  selectedTimeSlot: PropTypes.shape({
    eventId: PropTypes.string.isRequired,
    from: PropTypes.number.isRequired,
    to: PropTypes.number,
  }),
  selectedPartner: PropTypes.shape({}),
  timeSlotsList: PropTypes.func,
  timeSlotSectionTitle: PropTypes.string,
  formatTimeSlots: PropTypes.func,
  children: PropTypes.node.isRequired,
  loadNextSlots: PropTypes.func,
  loadPreviousSlots: PropTypes.func,
  areSlotsEqualFun: PropTypes.func.isRequired,
  eligibleSlotsFilter: PropTypes.func.isRequired,
  isFiltersHidden: PropTypes.bool,

  dispatchApplyFilters: PropTypes.func.isRequired,
  dispatchSelectDateSlot: PropTypes.func.isRequired,
  dispatchSetMainAppointmentTimeSlot: PropTypes.func.isRequired,
  dispatchSetRelatedAppointmentTimeSlot: PropTypes.func.isRequired,
  dispatchApiCallFinished: PropTypes.func.isRequired,
};

CalendarWithFilters.defaultProps = {
  filters: [],
  extraClassName: '',
  timeSlotsList: undefined,
  timeSlotSectionTitle: undefined,
  formatTimeSlots: undefined,
  loadNextSlots: undefined,
  loadPreviousSlots: undefined,
  selectedPartner: {},
  isFiltersHidden: false,
  isEditMode: false,
  relatedAppointmentType: null,
  selectedTimeSlot: undefined,
};
